import { createStore, applyMiddleware } from 'redux';
import thunk from 'redux-thunk';
import { durationToSecond } from '../../../common/utils/tools';
import { translate } from '../../../common/i18n';
import { isV4SyntheticToken, fetchV4WsUrl, makeV4OnReconnect, redirectToV4OAuthLogin } from '../../../common/appId';
import { createError } from '../../common/error';
import { expectInitArg, expectTradeOptions } from '../sanitize';
import { Martingale, Compound, OscarsGrind, Recover, Equilibrium, SmartMartingale, BinaryMartingale, Masaniello, MartingaleList, StakeList, ProfitCompound, LadderMartingale } from './money_managements';
import Patterns from './Patterns';
import Digits from './Digits';
import RiskManagement from './RiskManagement';
import Proposal from './Proposal';
import Total from './Total';
import Lists from './Lists';
import Balance from './Balance';
import OpenContract from './OpenContract';
import Sell from './Sell';
import Purchase from './Purchase';
import Ticks from './Ticks';
import rootReducer from './state/reducers';
import * as constants from './state/constants';
import { start } from './state/actions';
import { observer as globalObserver } from '../../../common/utils/observer';

const watchBefore = store =>
    watchScope({
        store,
        stopScope: constants.DURING_PURCHASE,
        passScope: constants.BEFORE_PURCHASE,
        passFlag : 'proposalsReady',
    });

const watchDuring = store =>
    watchScope({
        store,
        stopScope: constants.STOP,
        passScope: constants.DURING_PURCHASE,
        passFlag : 'openContract',
    });

/* The watchScope function is called randomly and resets the prevTick
 * which leads to the same problem we try to solve. So prevTick is isolated
 */
let prevTick;
const watchScope = ({ store, stopScope, passScope, passFlag }) => {
    // in case watch is called after stop is fired
    if (store.getState().scope === stopScope) {
        return Promise.resolve(false);
    }
    return new Promise(resolve => {
        const unsubscribe = store.subscribe(() => {
            const newState = store.getState();

            if (newState.newTick === prevTick) return;
            prevTick = newState.newTick;

            if (newState.scope === passScope && newState[passFlag]) {
                unsubscribe();
                resolve(true);
            }

            if (newState.scope === stopScope) {
                unsubscribe();
                resolve(false);
            }
        });
    });
};
export default class TradeEngine extends Lists(Digits(Patterns(RiskManagement(Masaniello(Equilibrium(Recover(SmartMartingale(BinaryMartingale(Martingale(OscarsGrind(MartingaleList(LadderMartingale(Compound(StakeList(Purchase(Sell(OpenContract(Proposal(Ticks(Total(ProfitCompound(Balance(class {}))))))))))))))))))))))) {
    constructor($scope) {
        super();
        this.api = $scope.api;
        this.virtualApi = $scope.virtualApi;
        this.observer = $scope.observer;
        this.$scope = $scope;
        this.observe();
        this.data = {
            contract         : {},
            proposals        : [],
            forgetProposalIds: [],
        };
        this.dataVirtual = {
            contract         : {},
            proposals        : [],
            forgetProposalIds: [],
        };
        this.store = createStore(rootReducer, applyMiddleware(thunk));
    }
    init(...args) {
        this.toStop = false;
        const [token, options] = expectInitArg(args);
        this.virtualSettings = options.virtualTrades || {};
        const { symbol } = options;
        this.initArgs = args;
        this.options = options;
        this.startPromise = this.loginAndGetBalance(token);
        this.watchTicks(symbol);
    }
    start = tradeOptions => {
        const options = tradeOptions;
        if (!this.options) {
            throw createError('NotInitialized', translate('Bot.init is not called'));
        }
        if (this.virtualSettings.active && this.virtualSettings.valid && this.virtualSettings.ongoing) {
            globalObserver.emit('bot.isVirtual', true);
        } else {
            globalObserver.emit('bot.isVirtual', false);
        }
        globalObserver.emit('bot.running');
        globalObserver.setState({ isRunning: true });
        if (this.virtualSettings.active && this.virtualSettings.valid && this.virtualSettings.ongoing) {
            options.amount = 1;
        }
        this.tradeOptions = expectTradeOptions(options);
        this.store.dispatch(start());
        this.checkLimits(options);
        this.makeProposals({ ...this.options, ...options });
        this.checkProposalReady();
    };

    loginAndGetBalance(token) {
        if (this.token === token) {
            return Promise.resolve();
        }
        const validVirtual = this.virtualSettings.token && this.virtualSettings.valid && this.virtualSettings.ongoing && this.virtualSettings.active;
        return new Promise(async resolve => {
            // v4: no api.authorize — switch the bot socket(s) onto the OTP-authenticated WS
            // (server-bearer, looked up by email). traderbots parity. v3 falls through below.
            if (isV4SyntheticToken(token)) {
                this.token = token;
                try {
                    const accountId = token.slice('v4:'.length);
                    // v4 has no `authorize` response; downstream code reads accountInfo.loginid.
                    this.accountInfo = { loginid: accountId, account_id: accountId };
                    const wsUrl = await fetchV4WsUrl(accountId);
                    const baseReconnect = makeV4OnReconnect();
                    const tracedReconnect = async () => {
                        console.log('[v4-trace] bot onReconnect FIRED — fetching fresh OTP url...');
                        try {
                            const u = await baseReconnect();
                            console.log('[v4-trace] bot onReconnect got fresh url=', (u || '').slice(0, 80));
                            return u;
                        } catch (err) {
                            console.log('[v4-trace] bot onReconnect FAILED:', err && err.message);
                            throw err;
                        }
                    };
                    await this.api.switchApiUrl({
                        apiUrl     : wsUrl,
                        rawApiUrl  : true,
                        timeoutMs  : 20000,
                        onReconnect: tracedReconnect,
                    });
                    console.log('[v4-trace] bot post-switch socket=', this.api.socket && this.api.socket.url, 'onReconnect=', typeof this.api.onReconnect);
                    this.api.events.on('open', () => console.log('[v4-trace] bot socket OPEN url=', this.api.socket && this.api.socket.url));
                    this.api.events.on('error', e => console.log(`[v4-trace] bot socket ERROR ${JSON.stringify(e)}`, e && (e.code || e.message || e)));

                    // v4 virtual trades: virtualApi must use the demo account's OTP socket too.
                    if (validVirtual && isV4SyntheticToken(this.virtualSettings.token)) {
                        try {
                            const virtualAccountId = this.virtualSettings.token.slice('v4:'.length);
                            const virtualWsUrl = await fetchV4WsUrl(virtualAccountId);
                            await this.virtualApi.switchApiUrl({
                                apiUrl     : virtualWsUrl,
                                rawApiUrl  : true,
                                timeoutMs  : 20000,
                                onReconnect: () => fetchV4WsUrl(virtualAccountId, { otpOnly: true }),
                            });
                            console.log('[v4-trace] virtualApi post-switch socket=', this.virtualApi.socket && this.virtualApi.socket.url);
                            this.virtualApi.subscribeToBalance().catch(() => {});
                            globalObserver.emit('bot.virtualApi', this.virtualApi);
                        } catch (ve) {
                            this.virtualSettings.valid = false;
                            globalObserver.emit('ui.log.error', translate('The token used on the Virtual Settings is invalid, disabling virtual trades'));
                        }
                    }

                    this.api
                        .subscribeToBalance()
                        .then(response => {
                            const {
                                balance: { balance, currency },
                            } = response;
                            console.log('[v4-trace] bot balance OK socket=', this.api.socket && this.api.socket.url);
                            if (balance != null && !Number.isNaN(Number(balance))) {
                                globalObserver.setState({ balance: Number(balance), currency });
                            }
                            resolve('real');
                        })
                        .catch(e => this.$scope.observer.emit('Error', e));
                } catch (e) {
                    this.$scope.observer.emit('Error', e);
                }
                return;
            }

            // Non-v4 tokens can no longer authorize: Deriv's current API has no WebSocket
            // authorize message, and a raw account token can't be exchanged for an OTP URL
            // (that requires an OAuth bearer). Send the user through the working v4 login
            // instead of hanging on a connect+authorize sequence that can never resolve.
            globalObserver.emit('ui.log.error', translate('Your session is outdated. Please log in again.'));
            this.$scope.observer.emit('Error', new Error('Legacy token can no longer authorize; redirecting to login'));
            redirectToV4OAuthLogin();
            resolve();
        });
    }
    // loginAndGetBalance(token) {
    //     if (this.token === token) {
    //         return Promise.resolve();
    //     }

    //     doUntilDone(() => this.api.authorize(token)).catch(e => this.$scope.observer.emit('Error', e));

    //     return new Promise(resolve =>
    //         this.listen('authorize', ({ authorize }) => {
    //             this.accountInfo = authorize;
    //             this.token = token;

    //             // Only subscribe to balance in browser, not for tests.
    //             if (document) {
    //                 this.api.subscribeToBalance().then(response => {
    //                     const {
    //                         balance: { balance, currency },
    //                     } = response;

    //                     globalObserver.setState({
    //                         balance: Number(balance),
    //                         currency,
    //                     });
    //                     resolve();
    //                 });
    //             } else {
    //                 resolve();
    //             }
    //         })
    //     );
    // }
    getContractDuration() {
        const { duration, duration_unit: durationUnit } = this.tradeOptions;

        return durationToSecond(`${duration}${durationUnit}`);
    }
    observe() {
        this.observeOpenContract();

        this.observeBalance();

        this.observeProposals();
    }
    watch(watchName) {
        if (watchName === 'before') {
            return watchBefore(this.store);
        }
        return watchDuring(this.store);
    }
    getData() {
        return this.data;
    }
    listen(n, f) {
        this.api.events.on(n, f);
    }
    listenVirtual(n, f) {
        this.virtualApi.events.on(n, f);
    }
}
